// Work at Height — per-template authored content. See ./types.ts for the content rule.
import type { TemplateSpecific } from "./types";

export const WORK_AT_HEIGHT: Record<string, TemplateSpecific> = {
  "wah-general": {
    whoFor:
      "Written for contractors and site teams who need a general work at height method statement and risk assessment to hand to a principal contractor or client before mobilising. It suits jobs where the access method varies or where height work is one element of a wider package, and the PC has asked for a documented plan showing the WAHR 2005 hierarchy has been applied. Use it as your baseline document and tailor the access-specific detail to the actual job.",
    scope:
      "This document covers general work carried out at height on a construction or maintenance site, including selecting an access method, preventing falls of people and objects, and managing the work zone below. It sets out the planning, supervision and equipment decisions required by the Work at Height Regulations 2005 for any task where a person could fall a distance liable to cause injury. It is a generic baseline and does not replace a task-specific RAMS for scaffolding, MEWPs, fragile roofs or rope access, which carry their own controls.",
    legislation: [
      "Work at Height Regulations 2005",
      "Management of Health and Safety at Work Regulations 1999",
      "Construction (Design and Management) Regulations 2015",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "HSE INDG401 — Working at height: A brief guide",
    ],
    sequence: [
      "Confirm the height work cannot reasonably be avoided or done from the ground; record the avoidance decision before selecting access.",
      "Apply the WAHR hierarchy: prevent falls with collective measures (working platforms, guardrails) before personal protection, and personal protection before mitigation.",
      "Select and justify the access method against duration, height and the surface being worked, and assign a competent person to oversee it.",
      "Establish an exclusion zone beneath the work and brief everyone on the daily permit, weather limits and the stop-work trigger.",
      "Inspect access equipment and edge protection at the start of each shift and after any event that could affect stability.",
      "Carry out the work with materials secured against falling and tools tethered or contained where there is anyone below.",
      "Stand the job down in adverse weather, on the supervisor's instruction, and at the end of the shift with the platform left safe.",
    ],
    hazards: [
      {
        hazard: "Fall from an unprotected working position or open edge",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Avoid the height work where the task can be done from ground level",
          "Provide a working platform with double guardrail and toe board as the primary control",
          "Use a fall-restraint system only where collective protection is not reasonably practicable",
          "Supervise the work and prohibit access until edge protection is verified in place",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Selecting an access method unsuited to the duration or surface",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Assess height, duration and ground/surface condition before choosing access",
          "Record the justification for the method against the WAHR hierarchy",
          "Have a competent person sign off the selection before work starts",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Objects or materials falling onto people below",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Establish and barrier an exclusion zone beneath the work area",
          "Fit toe boards or brick guards and contain loose materials in the platform",
          "Tether hand tools and lower materials rather than dropping or throwing",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Work continuing in high wind or other adverse weather",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Set a wind-speed stop limit appropriate to the access equipment in use",
          "Monitor the forecast and check conditions before and during the shift",
          "Stand work down and leave the platform safe when limits are exceeded",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
    ],
    ppe: [
      "Safety helmet with chin strap (EN 397)",
      "Class 2/3 hi-vis where there is plant or traffic movement",
      "Non-slip safety footwear (EN ISO 20345)",
      "Gloves appropriate to the materials being handled",
    ],
    competence: [
      "Operatives briefed on the WAHR 2005 hierarchy and this method statement",
      "Nominated competent person to select and inspect the access equipment",
      "Supervisor with authority to stop work in adverse weather",
    ],
    permits: [
      "Daily access permit confirming equipment inspected and weather acceptable",
    ],
    equipment: [
      "Selected working platform appropriate to the task",
      "Double guardrail and toe board sets",
      "Exclusion-zone barriers and signage",
      "Tool tethers or tool containment bags",
      "Anemometer for wind-speed monitoring",
    ],
    pcChecks: [
      "That the avoid-the-height decision is recorded, not skipped straight to a ladder or harness",
      "That the chosen access method is justified against duration and the working surface",
      "That an exclusion zone and falling-object controls are specified, not just edge protection",
    ],
    faqs: [
      {
        q: "What height counts as 'work at height' under the regulations?",
        a: "The Work at Height Regulations 2005 do not set a minimum height. Work at height is any work where a person could fall a distance liable to cause personal injury — that can include working below ground level, such as in an excavation. There is no 'two metre rule'; the old threshold was removed in 2005. You must assess and control the risk for any such task regardless of the fall distance.",
      },
      {
        q: "Does a method statement have to follow the work at height hierarchy?",
        a: "Yes. Regulation 6 requires you to avoid work at height where reasonably practicable, then prevent falls using collective measures such as guardrails before personal protection, and only then mitigate the distance and consequences of a fall. A RAMS that jumps straight to a harness without showing why a working platform was not used will usually be rejected by a reviewer. The RamsDocs builder structures the controls in this order so the hierarchy is visible on the page.",
      },
    ],
  },

  "wah-mobile-tower": {
    whoFor:
      "Aimed at trades who assemble and work from a mobile aluminium tower scaffold — painters, M&E installers, ceiling fixers and maintenance teams. If the principal contractor or building manager has asked for a PASMA-compliant RAMS before you bring a tower on site, this document evidences the 3T or advance-guardrail build method and the inspection regime. It assumes a proprietary tower used within the manufacturer's instruction manual.",
    scope:
      "This covers the assembly, use, repositioning and dismantling of a mobile prefabricated access tower built to BS EN 1004-1. It addresses the safe build method, stabiliser and outrigger use, the maximum platform height to base ratio, and the inspection and tagging regime. It does not cover bespoke or system scaffold erected by scaffolders, nor towers used as a base for further height work such as harness anchorage.",
    legislation: [
      "Work at Height Regulations 2005",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "BS EN 1004-1:2020 — Mobile access and working towers",
      "PASMA Code of Practice for the safe use of mobile access towers",
      "Construction (Design and Management) Regulations 2015",
    ],
    sequence: [
      "Check the tower is the correct components for a single manufacturer's system and read the instruction manual on site.",
      "Assess the ground: firm, level and able to take the load, with sole boards under castors on soft or made-up ground.",
      "Build using the 3T (through-the-trap) or advance-guardrail method so the operative is never on an unguarded platform.",
      "Fit stabilisers or outriggers as the manual requires and keep within the maximum height-to-base ratio.",
      "Lock all castor brakes, fit the trap-door platform and guardrails, and complete a Scafftag before first use.",
      "Reposition only after clearing the platform of people and materials and checking for overhead lines and floor openings.",
      "Dismantle in reverse using the same 3T method, lowering components rather than dropping them.",
    ],
    hazards: [
      {
        hazard: "Operative on an unguarded platform during assembly or dismantling",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Build and dismantle using the 3T or advance-guardrail method only",
          "Never stand on a platform without the guardrails for that level fitted first",
          "Use only PASMA-trained operatives for assembly and alteration",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Tower overturning from overloading or exceeding the height-to-base ratio",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Fit the stabilisers or outriggers specified in the manufacturer's manual",
          "Stay within the maximum platform height for the base dimension",
          "Do not apply side loads, attach sheeting, or use the tower to support other work",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Tower moving while occupied or castor brakes left off",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Lock all castor brakes before anyone climbs the tower",
          "Clear the platform of people and materials before repositioning",
          "Move the tower by hand at the base only, never by pulling at height",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Using mismatched or damaged tower components",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use components from a single manufacturer's system only",
          "Inspect frames, braces and platforms for damage before assembly",
          "Quarantine bent or cracked components and report shortfalls",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Repositioning a tower into overhead power lines or floor openings",
        who: ["Operatives"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Survey the route for overhead services and floor penetrations before moving",
          "Reduce the tower height before moving where headroom is restricted",
          "Cover or barrier floor openings on the move route",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "Safety helmet with chin strap (EN 397)",
      "Non-slip safety footwear (EN ISO 20345)",
      "Gloves for component handling",
      "Hi-vis where moving plant is present",
    ],
    competence: [
      "PASMA-trained operatives (in-date card) for assembly, alteration and dismantling",
      "Operatives briefed on this method statement and the specific tower's manual",
      "Competent person to carry out and record the pre-use and weekly inspection",
    ],
    permits: [
      "Scafftag / tower inspection tag completed before first use and after any move or alteration",
    ],
    equipment: [
      "Proprietary aluminium tower frames, braces and trap-door platforms (single system)",
      "Stabilisers or outriggers to the manufacturer's specification",
      "Lockable castors with sole boards for soft ground",
      "Toe boards for all four sides of the working platform",
      "Scafftag inspection tag holder",
    ],
    pcChecks: [
      "That the build method is stated as 3T or advance-guardrail, not 'build it up and climb inside'",
      "That stabiliser/outrigger use and the height-to-base ratio are explicit for the tower being used",
      "That operatives hold in-date PASMA cards and the inspection/tagging regime is named",
    ],
    faqs: [
      {
        q: "Do I need a PASMA card to put up a mobile tower?",
        a: "There is no law that names PASMA specifically, but PUWER and the Work at Height Regulations require anyone assembling, altering or dismantling a tower to be competent. PASMA training is the recognised way to demonstrate that competence, and most principal contractors will refuse access without an in-date card. The card lasts five years. If you cannot show competent assembly, expect your RAMS and your access to be rejected at the gate.",
      },
      {
        q: "What is the 3T method and why does the RAMS have to mention it?",
        a: "3T stands for 'through the trap' — the operative positions themselves at the trap door of the platform to add or remove the guardrails for the next level, so they are never standing on an unprotected platform. The alternative is the advance-guardrail method, where a temporary guardrail is locked in place from below before you climb up. A RAMS that does not specify one of these methods implies someone stands on an open platform to build the next lift, which is exactly the scenario that causes tower fatalities, so reviewers look for it.",
      },
    ],
  },

  "wah-mewp": {
    whoFor:
      "For operators and supervisors using a mobile elevating work platform — scissor lift, boom or cherry picker — on construction, facilities or arboriculture sites. Where the principal contractor requires a MEWP RAMS with a rescue plan before the machine is delivered, this document covers the IPAF categories, the LOLER thorough examination and the all-important recovery of a stranded operator. It assumes a hired or owned machine operated within its rated capacity.",
    scope:
      "This covers the use of a self-propelled or trailer MEWP to provide a temporary working platform at height, including familiarisation, ground assessment, operation and emergency recovery. It addresses entrapment, overturning, the rescue plan for a stranded or incapacitated operator, and the LOLER examination certificate. It does not cover using the MEWP as a crane to lift materials, nor exiting the basket to work at height unless a separate working-at-height-from-MEWP assessment is in place.",
    legislation: [
      "Work at Height Regulations 2005",
      "Lifting Operations and Lifting Equipment Regulations 1998 (LOLER)",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "BS 8460:2017 — Safe use of MEWPs, Code of Practice",
      "IPAF / HSE GEIS6 — Avoiding trapping/crushing injuries to people in MEWPs",
    ],
    sequence: [
      "Confirm the operator holds the correct IPAF category (3a mobile vertical or 3b mobile boom) and is familiarised on the specific machine.",
      "Check the LOLER thorough examination certificate is in date and complete the daily pre-use inspection and function test.",
      "Assess the ground for bearing capacity, gradient, voids, drains and underground services before deploying outriggers.",
      "Survey overhead for power lines and structures and set the work zone to keep the basket clear of crushing points.",
      "Operate within the rated capacity and reach, wearing a restraint lanyard clipped to the designated basket anchor on a boom.",
      "On a planned interruption or emergency, lower using the ground-level auxiliary/emergency controls under the rescue plan.",
      "Recover a stranded operator promptly using the auxiliary descent — never by waiting on the emergency services.",
    ],
    hazards: [
      {
        hazard: "Operator entrapment or crushing against a fixed structure overhead",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Plan the work zone to keep the basket clear of overhead crushing hazards",
          "Use a machine with secondary guarding or anti-entrapment where overhead work is unavoidable",
          "Operate at slow speed near structures and keep a second person able to use ground controls",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "MEWP overturning on soft, sloping or undermined ground",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Assess ground bearing capacity, voids and services before positioning",
          "Deploy outriggers on spreader pads and confirm the machine reads level",
          "Stay within the rated reach and do not exceed the gradient limit in the manual",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Operator stranded at height by machine failure or incapacitation",
        who: ["Operatives"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Brief a rescue plan that uses the ground-level auxiliary descent, not a 999 call",
          "Train a ground person to operate the emergency lowering controls",
          "Keep the basket clear at ground level so a lowered operator is not trapped",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Contact with overhead power lines while slewing or elevating",
        who: ["Operatives"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Survey for overhead lines and agree exclusion distances with the network operator",
          "Set physical goal posts or barriers to limit machine reach toward lines",
          "Brief the operator on the minimum approach distance for the line voltage",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "Restraint lanyard and full-body harness clipped to the basket anchor (boom MEWPs)",
      "Safety helmet with chin strap (EN 397)",
      "Class 2/3 hi-vis clothing",
      "Non-slip safety footwear (EN ISO 20345)",
    ],
    competence: [
      "IPAF-certified operator (3a and/or 3b) familiarised on the specific machine",
      "Trained ground rescuer able to operate the emergency lowering controls",
      "Supervisor competent to assess ground conditions and authorise positioning",
    ],
    permits: [
      "Permit to use confirming LOLER certificate in date and pre-use inspection complete",
    ],
    equipment: [
      "MEWP of the correct type (scissor, vertical mast or articulated boom) for the reach",
      "Outrigger spreader pads / sole boards for ground loading",
      "Full-body harness with adjustable restraint lanyard and basket anchor point",
      "Ground-level emergency/auxiliary lowering controls (machine-fitted)",
      "Overhead-line goal posts or exclusion barriers where lines are present",
    ],
    pcChecks: [
      "A rescue plan that recovers a stranded operator using the machine's auxiliary descent, not by calling the fire service",
      "That the LOLER thorough examination certificate is current (within six months) and referenced",
      "That the operator's IPAF category matches the machine type — 3a for verticals, 3b for booms",
    ],
    faqs: [
      {
        q: "Why does a MEWP RAMS need a rescue plan if the machine has emergency lowering?",
        a: "Because the operator may be the person who is incapacitated — slumped over the controls after a medical event or trapped against a structure — so they cannot operate the emergency descent themselves. The Work at Height Regulations require you to plan for emergencies and rescue, and a plan that relies on a 999 call ignores how long fire-service rescue from height actually takes. A workable plan trains a ground person to bring the basket down on the auxiliary controls and accounts for suspension trauma if a harness is in use. Reviewers treat the rescue plan as the heart of a MEWP RAMS.",
      },
      {
        q: "Should I wear a harness in a scissor lift?",
        a: "Generally no fall-arrest harness is needed in a scissor lift — the enclosed guardrails are the primary protection (BS 8460), and a trailing lanyard can create its own trip and snag hazards. A boom MEWP is different: it can catapult an operator out of the basket if the boom strikes an obstruction, so a restraint lanyard clipped to the basket anchor is standard practice on booms. In both cases follow the machine manufacturer's instructions and any site rules, which your RAMS should reference.",
      },
    ],
  },

  "wah-ladders": {
    whoFor:
      "Suited to electricians, decorators, signage fitters and maintenance staff who use ladders and stepladders for short-duration, low-risk tasks. When a client or principal contractor questions why a ladder is being used instead of a tower or MEWP, this document records the justification and the three points of contact / footing controls. It is for light access work, not for jobs that should be on a platform.",
    scope:
      "This covers the use of leaning ladders and stepladders for low-risk, short-duration work — typically under 30 minutes in one position — where heavier access cannot reasonably be justified. It addresses footing, angle, securing, overreaching and the condition of the ladder itself. It does not cover using ladders as a working platform for prolonged work, carrying heavy loads up a ladder, or work that imposes a side load such as drilling into masonry.",
    legislation: [
      "Work at Height Regulations 2005 — Schedule 6 (ladders)",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "BS EN 131 — Ladders (Professional / non-domestic duty)",
      "HSE INDG455 — Safe use of ladders and stepladders",
      "Management of Health and Safety at Work Regulations 1999",
    ],
    sequence: [
      "Justify the ladder against the WAHR risk assessment — low risk, short duration and no reasonable alternative platform.",
      "Select an EN 131 Professional / Class 1 ladder of the right length and inspect it for damage and intact feet.",
      "Set a leaning ladder at the 75-degree (1 in 4) angle on firm, level, non-slippery ground.",
      "Secure the ladder by tying it at the stiles, or use a stability device and a second person footing it where tying is not possible.",
      "Maintain three points of contact and keep the belt buckle within the stiles to prevent overreaching.",
      "Limit the time in one position and reposition the ladder rather than stretching sideways.",
      "Stand the ladder down in wind or wet conditions that compromise grip or stability.",
    ],
    hazards: [
      {
        hazard: "Ladder slipping outward at the base or sideways at the top",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Set the ladder at 75 degrees on firm, level, non-slippery ground",
          "Tie the ladder at the stiles or use a stability device against an upper resting point",
          "Foot the ladder with a second person only where securing is not reasonably practicable",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Overreaching causing loss of balance and a fall",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Keep the belt buckle (navel) inside the stiles at all times",
          "Reposition the ladder rather than stretching to the side",
          "Maintain three points of contact and grip the ladder when reaching",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Using a damaged, wrong-class or makeshift ladder",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use only EN 131 Professional / Class 1 ladders rated for the load",
          "Pre-use inspect stiles, rungs, feet and any locking mechanism",
          "Withdraw and label any ladder with bent stiles, worn feet or missing rungs",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Side loading or top-heavy work toppling a stepladder",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Avoid work that imposes a side load such as drilling into masonry from a step",
          "Open the stepladder fully and lock the spreader before climbing",
          "Do not work from the top two steps unless there is a suitable handhold",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
    ],
    ppe: [
      "Non-slip safety footwear with clean, dry soles (EN ISO 20345)",
      "Safety helmet where there is overhead or falling-object risk",
      "Gloves suited to the task (removed if they reduce grip on the rungs)",
    ],
    competence: [
      "Operatives trained in safe ladder use and this method statement",
      "Person nominated to carry out and record the pre-use ladder inspection",
      "Supervisor able to challenge ladder use where a platform is more appropriate",
    ],
    permits: [
      "No permit normally required; record the ladder justification on the daily risk assessment",
    ],
    equipment: [
      "EN 131 Professional (Class 1) leaning ladder of correct length",
      "EN 131 Professional stepladder with locking spreaders",
      "Ladder stability device or stand-off (where the top cannot be tied)",
      "Proprietary ladder ties / rope for securing at the stiles",
      "Ladder feet / anti-slip mat for hard smooth floors",
    ],
    pcChecks: [
      "That the ladder is justified as low-risk and short-duration, not used as a default for prolonged work",
      "That securing is specified — tied at the stiles or a stability device — not just 'footed by a colleague'",
      "That the ladders are EN 131 Professional class, not domestic or homemade",
    ],
    faqs: [
      {
        q: "Is it illegal to work from a ladder?",
        a: "No. There is a persistent myth that ladders were banned, but the Work at Height Regulations 2005 expressly permit them. Schedule 6 allows ladders where a risk assessment shows the use of more suitable equipment is not justified because the work is low risk and short duration. The regulations set no time limit; HSE guidance (INDG455) treats roughly 30 minutes in one position as the working interpretation of short duration. The duty is to justify the ladder, not to avoid it altogether.",
      },
      {
        q: "Does a ladder have to be tied, or can someone foot it?",
        a: "Tying or otherwise securing the ladder is the preferred control because footing is unreliable above about three metres — a person at the base cannot stop a ladder slipping under load. You should secure a leaning ladder at the stiles near the top, or use a stability device or stand-off where there is nothing to tie to. Footing by a second person is only acceptable where securing is not reasonably practicable and the ladder is short. A RAMS that lists footing as the only control on a long ladder will be questioned.",
      },
    ],
  },

  "wah-scaffold-erect": {
    whoFor:
      "For scaffolding contractors and their squads erecting, altering and striking tube-and-fitting or system scaffold. Where the principal contractor requires a scaffold RAMS to SG4:22 before the scaffold gangs start, this document covers the leading-edge controls during the build, the design/handover and the tag regime. It is written for CISRS-carded scaffolders, not for trades using a completed scaffold.",
    scope:
      "This covers the erection, alteration and dismantling of access and working scaffold, including handling tube, fittings and boards at height, controlling the leading edge before guardrails exist, and handing over a tagged, inspected structure. It addresses the SG4:22 safe system of work and the TG20:21 compliance or bespoke design route. It does not cover the use of the completed scaffold by following trades, who work under their own RAMS, nor suspended or hanging scaffolds.",
    legislation: [
      "Work at Height Regulations 2005",
      "NASC SG4:22 — Preventing falls in scaffolding operations",
      "NASC TG20:21 — Good practice guidance for tube and fitting scaffolding",
      "Construction (Design and Management) Regulations 2015",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
    ],
    sequence: [
      "Confirm the scaffold is TG20:21 compliant or has a bespoke design, and check ground bearing and base plate / sole board provision.",
      "Erect the first lift, establishing standards, ledgers and transoms and fitting node-point fall protection per SG4:22.",
      "Fit the guardrails and intermediate rail at each lift before boarding out and working from that level.",
      "Use the SG4:22 safe zone / advanced guardrail and a fall-arrest lanyard clipped to a sound anchor while exposed to the leading edge.",
      "Build up lift by lift, tying the scaffold to the structure at the design pattern and bracing as you go.",
      "Board out fully, fit toe boards and brick guards, and complete the design check on the finished structure.",
      "Tag the scaffold (Scafftag) on handover and re-tag after any alteration; dismantle in reverse without removing ties prematurely.",
    ],
    hazards: [
      {
        hazard: "Fall from the leading edge before guardrails are fitted",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Work to the SG4:22 safe system using node-point or advanced guardrail protection",
          "Clip a fall-arrest lanyard to a sound anchor while exposed at the working lift",
          "Fit guardrails at each lift before boarding out and standing on that level",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Manual handling of scaffold tubes, boards and fittings at height",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Use a gin wheel or hoist for raising tubes and boards rather than passing by hand",
          "Pass components hand to hand within a team and keep loads within safe limits",
          "Rotate tasks and plan lifts to limit repetitive overhead handling",
        ],
        residual: { likelihood: 3, severity: 2 },
      },
      {
        hazard: "Scaffold collapse from missing ties, bracing or overloading",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Erect to a TG20:21-compliant configuration or a bespoke design",
          "Install ties to the design pattern as the scaffold rises, never removed early on strike",
          "Confirm ground bearing and base plate / sole board provision before loading",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Following trades using an incomplete or untagged scaffold",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Display a Scafftag showing the scaffold incomplete / do-not-use until handover",
          "Tag the structure as complete only after the design and handover inspection",
          "Re-tag after any alteration and seal off partial scaffolds out of hours",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Dropped tube or fitting striking people below",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Barrier an exclusion zone at ground level beneath the erection face",
          "Fit toe boards and brick guards as boarding progresses",
          "Lower components on the gin wheel and never drop tubes or fittings",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
    ],
    ppe: [
      "Full-body harness with fall-arrest lanyard for leading-edge work (SG4:22)",
      "Safety helmet with chin strap (EN 397)",
      "Class 2/3 hi-vis clothing",
      "Scaffolder's gloves and non-slip safety footwear (EN ISO 20345)",
    ],
    competence: [
      "CISRS-carded scaffolders for the grade of scaffold being erected",
      "CISRS Advanced scaffolder for complex / bespoke designs",
      "Competent person for the design check and handover inspection",
    ],
    permits: [
      "Scaffold handover certificate and Scafftag completed before the structure is used",
    ],
    equipment: [
      "Steel tube, couplers and boards (or proprietary system components)",
      "Gin wheel and rope (or scaffold hoist) for raising materials",
      "SG4:22 advanced guardrail / safe-zone equipment",
      "Full-body harness with fall-arrest lanyard and energy absorber",
      "Base plates, sole boards and adjustable bases for ground loading",
    ],
    pcChecks: [
      "That the build references SG4:22 leading-edge protection, not just 'harness when needed'",
      "That the scaffold is TG20:21 compliant or has a bespoke design with a competent design check",
      "That the tagging regime keeps following trades off the scaffold until it is handed over",
    ],
    faqs: [
      {
        q: "What is the difference between TG20:21 and a bespoke scaffold design?",
        a: "TG20:21 is the NASC good-practice suite that, with its eGuide, lets a competent scaffolder build common tube-and-fitting configurations to a compliant standard without a bespoke calculation. If the scaffold falls outside the TG20 scope — heavy loading, unusual geometry, sheeting or special ties — it needs a bespoke design from a temporary works designer. Your RAMS should state which route applies and reference the TG20 compliance sheet or the design number. A reviewer will ask for one or the other.",
      },
      {
        q: "Does SG4:22 mean scaffolders must clip on for the whole build?",
        a: "SG4:22 sets out a hierarchy where collective protection — advanced guardrails and a defined safe zone — is the primary control, and personal fall arrest is used to protect the scaffolder only while they are exposed to the leading edge and cannot yet be behind a guardrail. It is not a blanket 'clip on all the time' rule, and it is not an excuse to skip guardrails. The point is to minimise the time anyone is reliant on a harness. Your method statement should describe the safe zone and when the lanyard is clipped.",
      },
    ],
  },

  "wah-roof-pitched": {
    whoFor:
      "Written for roofing contractors, slaters, tilers and solar installers working on sloping roofs. When the principal contractor or homeowner's surveyor asks for a roof work method statement before the job starts, this document covers edge protection, roof ladders and the controls that keep operatives and materials off the people below. It assumes a sound, non-fragile pitched roof; fragile sheeting is covered by a separate RAMS.",
    scope:
      "This covers work on a pitched roof of sound construction — stripping, re-covering, repairs, leadwork and rooftop installations — using edge protection, roof ladders or crawling boards and a managed materials route. It addresses falls from the eaves and verges, slips down the slope, and materials falling to the ground or into the building. It does not cover fragile roofs, rooflights or asbestos cement sheeting, which require the fragile-roof RAMS and ACR(M)001 controls.",
    legislation: [
      "Work at Height Regulations 2005",
      "HSE HSG33 — Health and safety in roof work",
      "Construction (Design and Management) Regulations 2015",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "Control of Substances Hazardous to Health Regulations 2002 (where bitumen / sealants used)",
    ],
    sequence: [
      "Survey the roof structure and pitch, confirm it is non-fragile, and check the weather window before access.",
      "Erect independent scaffold with the working platform close to eaves level and double guardrails to the verges.",
      "Provide edge protection at the eaves and at any gable or valley where a person could fall.",
      "Use a properly secured roof ladder or crawling boards to spread load and provide footing on the slope.",
      "Set up a materials route — chute or hoist to a skip — so debris and tiles never travel via the edge by hand.",
      "Work down the slope methodically, keeping the platform clear and removing waste as you go.",
      "Sheet or secure loose materials and the open roof against wind at the end of each shift.",
    ],
    hazards: [
      {
        hazard: "Fall from the eaves, verge or valley edge",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Erect independent scaffold with the platform close to eaves level",
          "Fit double guardrails and toe boards to eaves, gables and valleys",
          "Keep operatives within the protected zone and brief the no-go areas",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Slip or slide down the roof slope",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Use a secured roof ladder or crawling boards to provide footing and spread load",
          "Stop work when the roof surface is wet, frosted, mossy or iced",
          "Wear footwear with good grip and keep the working area clear of loose tiles",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Tiles, slates or debris falling to the ground or into the building",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Run debris and tiles via a covered chute or hoist into a skip, not over the edge",
          "Barrier an exclusion zone at ground level around the building",
          "Fit toe boards and netting to the scaffold to catch dropped material",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Wind lifting sheets, felt or stacked materials on the open roof",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Set a wind-speed stop limit and monitor the forecast through the shift",
          "Secure or sheet loose materials and the exposed roof at the end of each shift",
          "Do not handle large sheets or felt rolls in gusty conditions",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Safety helmet with chin strap (EN 397)",
      "Roofing-grade footwear with high-grip soles",
      "Gloves for handling tiles, slate and leadwork",
      "Class 2/3 hi-vis clothing",
    ],
    competence: [
      "Roofers experienced in pitched-roof work and briefed on this method statement",
      "Operative competent to inspect and secure the roof ladder / crawling boards",
      "Supervisor able to call the weather stop and verify edge protection",
    ],
    permits: [
      "Roof access permit confirming edge protection in place and weather acceptable",
    ],
    equipment: [
      "Independent access scaffold with eaves-level working platform",
      "Roof ladder with ridge hook (and crawling boards as required)",
      "Debris chute or hoist with receiving skip",
      "Scaffold netting and toe boards / brick guards",
      "Tile / slate hoisting equipment",
    ],
    pcChecks: [
      "That the roof is confirmed non-fragile, with fragile elements (rooflights) referred to the fragile-roof RAMS",
      "That a materials route (chute/hoist) is specified so debris does not travel via the edge by hand",
      "That edge protection covers verges and valleys, not just the eaves",
    ],
    faqs: [
      {
        q: "Do I need a full scaffold to re-roof a house, or will a roof ladder do?",
        a: "A roof ladder gives you footing on the slope but it does nothing to stop a fall from the eaves, so it is not edge protection. HSG33 expects collective protection at the perimeter — typically an independent scaffold with the platform at eaves level and guardrails — for anything beyond very short-duration repairs. The roof ladder works alongside the scaffold, not instead of it. A RAMS proposing only a roof ladder for a re-roof will be rejected.",
      },
      {
        q: "How do I deal with the rooflights in an otherwise sound pitched roof?",
        a: "Rooflights are fragile even when the surrounding roof is sound, and people fall through them every year, so they cannot be ignored. You should treat each rooflight as a fragile element — cover it with a load-bearing cover or stage board, or fit guard rails around it — and reference the fragile-roof controls for that part of the work. If much of the roof is fragile sheeting, use the dedicated fragile-roof RAMS instead. Identify the rooflight positions on your survey before access.",
      },
    ],
  },

  "wah-fragile-roof": {
    whoFor:
      "For contractors working on or near fragile roofs — asbestos cement sheet, fibre cement, old skylights and corroded metal decking — typically on industrial, agricultural and warehouse buildings. Falling through a fragile roof kills more workers in the UK each year than falls from the edge, so principal contractors scrutinise this RAMS hard. It is written for teams who have assumed every roof is fragile until proven otherwise.",
    scope:
      "This covers access to and work on roofs that are, or may be, fragile, including inspection, repair, cleaning and the installation of safe access. It addresses falls through the roof surface and through rooflights using staging, crawling boards and netting, and the ACR(M)001 non-fragility test for any sheet relied upon to bear weight. It does not cover the removal of asbestos cement sheeting as licensed work, which requires a separate plan of work under CAR 2012.",
    legislation: [
      "Work at Height Regulations 2005",
      "HSE HSG33 — Health and safety in roof work (fragile surfaces)",
      "ACR(M)001 — Test for non-fragility of large element roofing assemblies",
      "Construction (Design and Management) Regulations 2015",
      "Control of Asbestos Regulations 2012 (where asbestos cement is present)",
    ],
    sequence: [
      "Treat the whole roof as fragile until a competent survey or ACR(M)001 evidence proves otherwise.",
      "Identify and mark fragile sheets, rooflights and skylights, including those painted over or hidden by debris.",
      "Plan access from below using a MEWP or independent scaffold where reasonably practicable to avoid going onto the roof.",
      "Where roof access is unavoidable, lay staging or crawling boards spanning the load-bearing purlins to spread weight.",
      "Install fall-arrest netting beneath the work area or cover/guard every rooflight before anyone goes near it.",
      "Move only on the boarded route, never stepping onto the sheets between boards or onto a rooflight.",
      "Recover staging and netting on completion and re-cover any opening left in the roof.",
    ],
    hazards: [
      {
        hazard: "Falling through a fragile roof sheet",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Avoid going onto the roof — work from below using a MEWP or scaffold where practicable",
          "Lay staging or crawling boards spanning the load-bearing purlins to spread load",
          "Install fall-arrest netting slung beneath the roof at the work area",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Falling through a rooflight or skylight",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Mark every rooflight, including painted-over and debris-covered ones, before access",
          "Fit load-bearing covers or guardrails to each rooflight before work begins",
          "Keep the boarded route clear of all rooflights and brief their positions",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Stepping off the boarded route onto unprotected fragile sheets",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Provide a continuous boarded route to and across the work position",
          "Brief operatives that no sheet is to be trusted to bear weight directly",
          "Supervise access and keep numbers on the roof to the minimum needed",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Disturbing asbestos cement during fragile-sheet work",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Identify asbestos cement from the asbestos register / survey before work",
          "Avoid breaking or cutting sheets; treat suspect sheets as containing asbestos",
          "Stop and refer licensable removal to a separate plan of work under CAR 2012",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
    ],
    ppe: [
      "Full-body harness and fall-arrest lanyard where a fixed anchor and netting are provided",
      "Safety helmet with chin strap (EN 397)",
      "Soft-soled, high-grip footwear to suit the roof surface",
      "Disposable coveralls and RPE where asbestos cement may be disturbed",
    ],
    competence: [
      "Roofers trained in fragile-roof work and the use of staging and netting",
      "Competent surveyor to assess fragility / apply ACR(M)001 evidence",
      "Asbestos-awareness trained operatives where ACMs may be present",
    ],
    permits: [
      "Roof access permit confirming staging/netting installed and rooflights covered",
    ],
    equipment: [
      "Staging boards / crawling boards spanning the purlins",
      "Fall-arrest safety netting and rigging",
      "Load-bearing rooflight covers / proprietary rooflight guards",
      "MEWP for access from below where practicable",
      "Fragile-roof warning signage for the building perimeter",
    ],
    pcChecks: [
      "That the RAMS assumes the roof is fragile until proven non-fragile by survey or ACR(M)001 evidence",
      "That rooflights are covered/guarded as a named hazard, not folded into 'falls from height'",
      "That access-from-below (MEWP/scaffold) is considered before any plan to walk the roof",
    ],
    faqs: [
      {
        q: "How do I know if a roof is fragile?",
        a: "Assume it is fragile until you have evidence otherwise. Asbestos cement and fibre-cement sheets, corroded metal decking, glass and plastic rooflights, and old liner panels are all commonly fragile, and age, corrosion and the fixings all reduce strength. A sheet can only be relied on to bear a person's weight if it passes the ACR(M)001 non-fragility test and remains in good condition. HSG33 is clear that more people are killed falling through fragile roofs than from any other roof-work cause, so the default is caution.",
      },
      {
        q: "Can I just wear a harness instead of putting boards and nets out?",
        a: "A harness alone is the weakest control and should not be the primary measure on a fragile roof. The hierarchy is to avoid going on the roof, then to provide a load-bearing platform — staging or crawling boards spanning the purlins — and safety netting to catch a fall, with personal fall arrest only as a back-up where a suitable anchor exists. A harness does nothing to stop you punching through a sheet in the first place, and a fall onto a short lanyard can still be fatal. Reviewers expect to see collective measures first; the RamsDocs builder orders the controls that way.",
      },
    ],
  },

  "wah-gutter": {
    whoFor:
      "Designed for facilities, maintenance and cleaning firms carrying out gutter clearance on commercial and domestic buildings. When a managing agent or principal contractor asks for a gutter cleaning risk assessment before the visit, this document covers the access choice — MEWP, tower or, for low work, a secured ladder — and the slip and biological hazards of the task. It is for cleaning and minor clearance, not roof repairs.",
    scope:
      "This covers the clearing of leaves, silt and debris from gutters, downpipes and valley gutters at height, using a MEWP, mobile tower or secured ladder depending on height and frequency. It addresses falls during access, slips from wet debris, and exposure to bird droppings and standing water. It does not cover gutter replacement, roof repairs or work on fragile roof surfaces, which require their own RAMS.",
    legislation: [
      "Work at Height Regulations 2005",
      "BS 8460:2017 — Safe use of MEWPs, Code of Practice",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH)",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "HSE INDG401 — Working at height: A brief guide",
    ],
    sequence: [
      "Survey the building to map gutter runs, downpipes and the height, and pick the access method for each run.",
      "For higher or longer runs use a MEWP or mobile tower; reserve secured ladders for low, short, occasional clearance.",
      "Position and set up the chosen access, checking ground conditions and overhead lines before deploying.",
      "Clear debris by hand or vacuum into a container at the platform — never drop it to the ground.",
      "Flush downpipes and check the flow, clearing blockages from the rodding eye where fitted.",
      "Wash and dispose of debris as potentially contaminated waste, and wash hands before eating or drinking.",
      "Reposition the access along the run rather than overreaching from one set-up.",
    ],
    hazards: [
      {
        hazard: "Fall while accessing or reaching the gutter line",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Use a MEWP or mobile tower for higher or longer gutter runs in preference to a ladder",
          "Where a ladder is justified for low work, tie it and keep within the stiles",
          "Reposition the access along the run rather than stretching sideways from one set-up",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Exposure to bird droppings, standing water and contaminated silt",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Wear gloves and eye protection and treat all debris as contaminated",
          "Bag debris and wash hands and forearms before eating, drinking or smoking",
          "Provide RPE where dust from dried droppings is likely to be disturbed",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Falling debris or tools striking people below",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Barrier the area below the work and keep the public clear",
          "Collect debris into a container at the platform, never dropping it to the ground",
          "Tether or contain hand tools used at height",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
      {
        hazard: "Contact with overhead lines or services running near the eaves",
        who: ["Operatives"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Survey for overhead cables and service entries before positioning access",
          "Keep the MEWP or tower the agreed safe distance from any line",
          "Use non-conductive tools near electrical service heads where present",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "Waterproof gloves resistant to contaminated debris",
      "Eye protection against splashes and dust",
      "Disposable RPE (FFP3) where dried droppings will be disturbed",
      "Non-slip safety footwear and hi-vis where required",
    ],
    competence: [
      "Operatives trained in the access method used (IPAF for MEWP, PASMA for tower)",
      "Operatives briefed on the biological/COSHH hazards of gutter debris",
      "Supervisor able to verify the access choice for each gutter run",
    ],
    permits: [
      "Work permit confirming access set up and the area below barriered",
    ],
    equipment: [
      "MEWP or mobile tower (or tied ladder for low, short runs)",
      "Gutter vacuum or hand scoop and grabbers",
      "Debris collection bags / container at the platform",
      "Downpipe rods for clearing blockages",
      "Sharps/biological waste bags for contaminated debris",
    ],
    pcChecks: [
      "That the access method is chosen per gutter run by height and duration, not a ladder for everything",
      "That the biological (bird droppings / COSHH) hazard is assessed, not just the fall",
      "That debris is collected at the platform rather than dropped, with the area below barriered",
    ],
    faqs: [
      {
        q: "Can I clean gutters from a ladder?",
        a: "Sometimes, but only for low, short-duration, occasional work where a ladder is justified under the Work at Height Regulations and it can be properly secured. Gutter cleaning tempts people to overreach along the run, which is a leading cause of ladder falls, so for anything more than a quick clear a MEWP or mobile tower is usually the safer and more efficient choice. Your risk assessment should justify the ladder against height, duration and frequency, and require it to be tied. For commercial buildings a powered platform is normally expected.",
      },
      {
        q: "Why does a gutter cleaning RAMS mention COSHH?",
        a: "Because gutters collect bird droppings, decaying leaves and standing water, which can carry bacteria and, when dried droppings are disturbed, airborne contaminants. COSHH requires you to assess and control exposure to these substances, typically with gloves, eye protection, RPE for dusty conditions, and good hand hygiene before eating or drinking. The debris should be bagged and disposed of as potentially contaminated waste. A gutter RAMS that only covers the fall and ignores the biological hazard is incomplete.",
      },
    ],
  },

  "wah-fall-arrest": {
    whoFor:
      "For teams who must rely on a personal fall-arrest system — harness and lanyard or inertia reel — where collective protection is genuinely not reasonably practicable, such as steel erection, isolated maintenance points and some leading-edge tasks. The first thing a principal contractor looks for in this RAMS is a rescue plan that does not depend on calling 999, because suspension trauma can incapacitate a suspended worker within minutes. It is the last line of the WAHR hierarchy, not a default.",
    scope:
      "This covers the use of personal fall-arrest equipment — full-body harness, energy-absorbing lanyard or fall-arrester on an anchor or lifeline — together with the prompt rescue of a person who has fallen and is suspended. It addresses anchor selection, fall-factor and clearance, LOLER inspection of the equipment, and a self-sufficient rescue plan. It does not cover rope access (IRATA) or where collective measures such as guardrails or nets could reasonably be provided instead.",
    legislation: [
      "Work at Height Regulations 2005",
      "Lifting Operations and Lifting Equipment Regulations 1998 (LOLER) — inspection of harnesses/lanyards",
      "BS 8437:2005 — Selection, use and maintenance of personal fall protection systems",
      "BS EN 363 — Personal fall protection systems",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
    ],
    sequence: [
      "Confirm collective protection (guardrail, net) is not reasonably practicable and record why fall arrest is used.",
      "Identify and verify a structural anchor of adequate strength above the user, and calculate the fall factor and required clearance below.",
      "Inspect the harness, lanyard, connectors and any lifeline (LOLER thorough examination in date plus a pre-use check).",
      "Fit and adjust the full-body harness, connect the energy-absorbing lanyard or fall-arrester to the anchor, and keep slack to a minimum.",
      "Work within the safe radius of the anchor so a fall cannot become a swing (pendulum) into a structure.",
      "On a fall, implement the rescue plan immediately to relieve suspension before trauma sets in, using the on-site rescue kit.",
      "Quarantine any equipment that has arrested a fall and record the event for investigation.",
    ],
    hazards: [
      {
        hazard: "Suspension trauma after a fall is arrested",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Hold a self-sufficient rescue plan that recovers the casualty within minutes, not a 999 call",
          "Provide on-site rescue equipment and trained rescuers for the shift",
          "Fit suspension-relief / trauma straps and brief the casualty to deploy them",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Anchor point failure or use of an inadequate anchor",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Use only a verified structural anchor of adequate strength above the user",
          "Have a competent person confirm or design the anchorage before use",
          "Connect to a certified lifeline or eyebolt, never to scaffold tube or services",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Insufficient fall clearance — striking the ground or a lower level",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Calculate fall-factor, lanyard deployment and clearance before connecting",
          "Anchor above head height to minimise free-fall distance",
          "Use a fall-arrester / inertia reel rather than a long lanyard where clearance is tight",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Pendulum (swing) fall into a structure off a side anchor",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Keep the work position within the safe working radius of the anchor",
          "Reposition the anchor or use a horizontal lifeline rather than working off to the side",
          "Brief operatives on the swing zone and obstructions in the fall path",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
    ],
    ppe: [
      "Full-body harness (EN 361) correctly sized and adjusted",
      "Energy-absorbing lanyard (EN 355) or guided/retractable fall-arrester (EN 360/353)",
      "Suspension-relief / trauma straps",
      "Safety helmet with chin strap (EN 397) and non-slip footwear",
    ],
    competence: [
      "Operatives trained in fall-arrest system selection, fitting and use (BS 8437)",
      "Competent person to verify or design the anchorage and clearance",
      "Trained rescuers able to execute the rescue plan within the trauma window",
    ],
    permits: [
      "Permit to work confirming anchor verified, clearance calculated and rescue team on site",
    ],
    equipment: [
      "Full-body harness with front and rear attachment points",
      "Energy-absorbing lanyard and/or retractable fall-arrester",
      "Certified anchor / eyebolt or temporary horizontal lifeline system",
      "Rescue kit (descender/recovery device) and casualty trauma straps",
      "Connectors and karabiners (EN 362) with locking gates",
    ],
    pcChecks: [
      "A rescue plan that does not rely on calling 999 — suspension trauma timelines make this the first thing reviewers look for",
      "That the anchor strength, fall-factor and required clearance are stated, not assumed",
      "That the harness and lanyard carry an in-date LOLER thorough examination and pre-use check",
    ],
    faqs: [
      {
        q: "Why isn't a harness on its own enough to work safely at height?",
        a: "Because a harness only mitigates a fall after it has happened; it does not prevent one, and it introduces its own hazards. The Work at Height Regulations put personal fall arrest near the bottom of the hierarchy, below avoiding the work, collective fall prevention such as guardrails, and collective mitigation such as nets. Using a harness also commits you to providing a verified anchor, adequate clearance and, crucially, a rescue plan — without all three the system is incomplete. It should only be chosen where collective protection is genuinely not reasonably practicable.",
      },
      {
        q: "What is suspension trauma and why does it drive the rescue plan?",
        a: "Suspension trauma — orthostatic intolerance — happens when a person hangs motionless in a harness after a fall: the leg straps restrict blood return, blood pools in the legs, and the casualty can lose consciousness and deteriorate within minutes, sometimes faster than the fire service can reach them. That is why the law and reviewers demand a rescue plan that recovers a suspended worker quickly using on-site equipment and trained people, rather than waiting for the emergency services. Trauma-relief straps buy a little time but are not a substitute for prompt rescue. The plan is the single most scrutinised part of a fall-arrest RAMS.",
      },
    ],
  },

  "wah-edge-protection": {
    whoFor:
      "Aimed at the squads who install and remove temporary edge protection — guardrail systems on slabs, steel decks, leading edges and excavations — ahead of the trades that work behind it. Because the installers themselves are exposed to the very edge they are protecting, the principal contractor checks how the first run is fitted before any guardrail exists. It is written for operatives competent in the proprietary system being used.",
    scope:
      "This covers the installation, inspection and removal of temporary edge protection to slabs, floor edges, leading edges, stair openings and excavations, using proprietary guardrail systems with mesh barriers or double rail and toe board. It addresses the installers' own exposure at the open edge, dropped components, and handing over a verified barrier to following trades. It does not cover permanent balustrades, scaffold guardrails forming part of a scaffold (covered by the scaffold RAMS), or fall-arrest work.",
    legislation: [
      "Work at Height Regulations 2005",
      "BS EN 13374 — Temporary edge protection systems",
      "Construction (Design and Management) Regulations 2015",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "HSE INDG401 — Working at height: A brief guide",
    ],
    sequence: [
      "Plan the installation sequence so operatives work from the protected side toward the open edge wherever possible.",
      "Select the EN 13374 class of system (A, B or C) to suit the edge angle and the energy of a potential fall.",
      "Install the first post and rail run for the installer from a position of safety, using fall arrest while exposed to the open edge.",
      "Fix posts at the design centres into the slab or counterweight bases, then fit the principal and intermediate guardrails and toe board / mesh.",
      "Work along the edge fitting the continuous barrier, checking each post fixing as you go.",
      "Inspect the completed run for rail continuity, gaps and fixing security, then hand it over to following trades.",
      "Remove the protection in reverse only when the permanent edge or the trade behind it no longer needs it, last man clipped on.",
    ],
    hazards: [
      {
        hazard: "Installer exposed to the open edge before the guardrail exists",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Sequence the work to install from the protected side toward the edge",
          "Clip a fall-arrest lanyard to a verified anchor while exposed at the leading edge",
          "Set the first post and rail from a position of safety before advancing",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Wrong class of system for the edge angle or fall energy",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Select EN 13374 Class A, B or C to match the surface angle and fall energy",
          "Follow the system manufacturer's design for post centres and fixings",
          "Have a competent person confirm the class against the edge before installation",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Components or fixings dropped onto people below",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Barrier an exclusion zone beneath the edge being protected",
          "Tether tools and pass posts and rails hand to hand rather than throwing",
          "Fit a toe board / mesh infill to retain small components once the run is up",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Following trades relying on an incomplete or under-fixed barrier",
        who: ["Operatives", "Public"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Inspect rail continuity, gaps and post fixings before handover",
          "Tag or sign the run as complete only after the handover inspection",
          "Do not remove any section until the trade behind it confirms it is no longer needed",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "Full-body harness and fall-arrest lanyard for first-run / leading-edge installation",
      "Safety helmet with chin strap (EN 397)",
      "Cut-resistant gloves for handling posts and rails",
      "Class 2/3 hi-vis clothing and non-slip footwear",
    ],
    competence: [
      "Operatives trained in the proprietary edge-protection system being installed",
      "Competent person to confirm the EN 13374 class and the fixing design",
      "Supervisor to carry out and record the pre-handover inspection",
    ],
    permits: [
      "Handover record confirming the edge protection inspected and complete before trades follow",
    ],
    equipment: [
      "Proprietary EN 13374 guardrail posts, principal and intermediate rails",
      "Mesh barrier panels or toe boards to retain components",
      "Post fixings — slab clamps, cast-in sockets or counterweight bases",
      "Fall-arrest harness and lanyard for the leading-edge first run",
      "Cordless fixing tools with tool tethers",
    ],
    pcChecks: [
      "How the installer is protected on the first run before any guardrail exists, not just the finished barrier",
      "That the EN 13374 class (A/B/C) matches the edge angle and is justified, not generic 'guardrail'",
      "That a handover inspection signs off rail continuity and fixings before trades rely on it",
    ],
    faqs: [
      {
        q: "What do the EN 13374 classes A, B and C mean?",
        a: "EN 13374 sorts temporary edge protection by the type of surface and the energy it must resist. Class A suits near-flat surfaces (up to about 10 degrees) and resists a static load — the common slab-edge guardrail. Class B handles steeper surfaces and lower-energy dynamic falls, and Class C is for steep or pitched surfaces where the system must arrest a sliding person, such as on a roof. Picking the wrong class — a Class A rail on a pitched roof — gives a false sense of protection, so your RAMS should state the class and why it fits the edge.",
      },
      {
        q: "How do edge-protection installers protect themselves before the rail is up?",
        a: "This is the crux of the job and the first thing a reviewer asks. The installation is sequenced so operatives work from an already-protected position toward the open edge wherever the layout allows, fitting the first posts and rails before stepping into the exposed zone. Where exposure to the open edge is unavoidable for the first run, a personal fall-arrest lanyard clipped to a verified anchor protects the installer until the collective barrier exists. A RAMS that shows the finished guardrail but is silent on how the first run goes up has missed the highest-risk part of the task.",
      },
    ],
  },
};
